import type { OpsPropertyFilter } from "@/lib/opsAdminApi"

interface OpsPropertyRow {
  property_id: string
  property_name: string
  property_short_id: string
  host_email: string
  pms_health: { status: string; provider: string; connected: boolean }
  calendar_health: { status: string; last_sync_at: string; next_booking: { start_date: string; end_date: string; booking_number: string; summary: string } | null }
  places_health: { status: string; count: number }
  display_health: { status: string; last_heartbeat: string; device_id: string }
  guidebook_health: { status: string; enabled: boolean; last_login_at: string }
  last_render_at: string
  open_issues: string[]
  issue_count: number
}

export function OpsPropertiesTable({
  rows,
  loading,
  filter,
  onFilterChange,
  onSelectProperty,
  selectedPropertyId,
}: {
  rows: OpsPropertyRow[]
  loading: boolean
  filter: OpsPropertyFilter
  onFilterChange: (filter: OpsPropertyFilter) => void
  onSelectProperty: (propertyId: string) => void
  selectedPropertyId: string
}) {
  return (
    <div className="section-card" style={{ padding: 0, overflow: "hidden" }}>
      <div className="ops-table-header">
        <div className="section-card__title" style={{ marginBottom: 0 }}>Property Ops</div>
        <div className="ops-chip-row">
          <FilterChip label="All" active={filter === "all"} onClick={() => onFilterChange("all")} />
          <FilterChip label="Needs Attention" active={filter === "needs_attention"} onClick={() => onFilterChange("needs_attention")} />
          <FilterChip label="Stale Devices" active={filter === "stale_devices"} onClick={() => onFilterChange("stale_devices")} />
          <FilterChip label="Job Failures" active={filter === "job_failures"} onClick={() => onFilterChange("job_failures")} />
        </div>
      </div>

      {loading ? (
        <div style={{ padding: "var(--sf-space-8)", textAlign: "center" }}>
          <div className="sf-spinner" style={{ margin: "0 auto" }} />
        </div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__title">No properties for this filter</div>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Property</th>
              <th>Host</th>
              <th>PMS</th>
              <th>Calendar</th>
              <th>Places</th>
              <th>Display</th>
              <th>Guidebook</th>
              <th>Last Render</th>
              <th>Open Issues</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.property_id}
                onClick={() => onSelectProperty(row.property_id)}
                style={{ cursor: "pointer", background: selectedPropertyId === row.property_id ? "var(--sf-primary-light)" : undefined }}
              >
                <td>
                  <div style={{ fontWeight: 600 }}>{row.property_name}</div>
                  <div style={{ fontSize: "var(--sf-text-xs)", color: "var(--sf-text-muted)" }}>{row.property_short_id}</div>
                </td>
                <td>{row.host_email || "—"}</td>
                <td><StatusTag status={row.pms_health.status}>{row.pms_health.provider || "none"}</StatusTag></td>
                <td>
                  <StatusTag status={row.calendar_health.status}>
                    {row.calendar_health.last_sync_at ? "Synced" : "No sync"}
                  </StatusTag>
                </td>
                <td>{row.places_health.count}</td>
                <td>
                  <StatusTag status={row.display_health.status}>
                    {row.display_health.last_heartbeat ? "Online" : "Never seen"}
                  </StatusTag>
                </td>
                <td>
                  <StatusTag status={row.guidebook_health.status}>
                    {row.guidebook_health.enabled ? "Enabled" : "Disabled"}
                  </StatusTag>
                </td>
                <td>{formatTime(row.last_render_at)}</td>
                <td>{row.issue_count > 0 ? row.open_issues.slice(0, 2).join(", ") : "None"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      className={`ops-filter-chip${active ? " ops-filter-chip--active" : ""}`}
      onClick={onClick}
    >
      {label}
    </button>
  )
}

function StatusTag({
  status,
  children,
}: {
  status: string
  children: string
}) {
  const cls = status === "healthy" ? "healthy" : status === "error" ? "error" : status === "warning" ? "warning" : "neutral"
  return (
    <span className={`status-pill status-pill--${cls}`}>
      <span className="status-pill__dot" />
      {children}
    </span>
  )
}

function formatTime(value: string) {
  const iso = value || ""
  if (!iso) return "—"
  const ts = Date.parse(iso)
  if (!Number.isFinite(ts)) return iso
  return new Date(ts).toLocaleString()
}
